/**
 * Presentational cards for `/stablecoins/invoices/settings` (ALL-5908,
 * page spec `invoicing-settings`).
 *
 * Nothing here fetches, decides or mutates: the page owns the read, the draft
 * and the save, and these components render what it hands them. That split is
 * what lets the refusal state be tested without a working save.
 *
 * The legal-name row is a value, never a field. The page spec classifies a
 * `kyb_legal_name_editable` page as a bug, and it is the one value on this
 * screen that comes from the org's KYB record rather than from the operator.
 */

import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input, Field } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import { NotAvailableState, PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { formatMoney } from "@/features/invoicing/money";
import type { InvoicingSettings } from "@/features/invoicing/types";
import { SETTINGS_COPY as COPY, isDeniedCode } from "./copy";
import { isCertified, type SettingsDraft } from "./settings-model";

/**
 * A refused read or write, rendered with the code the service sent and its
 * support reference. A denial gets the access-denied framing instead — the
 * operator's next step there is a permission, not a retry — but keeps the same
 * reason code and reference underneath.
 */
export function SettingsRefusal({
  reasonCode,
  message,
  supportReference,
  capability,
  onRetry,
  retryLabel = "Try again",
  className,
}: {
  reasonCode: string;
  message?: string;
  supportReference?: string;
  capability?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}) {
  // Only a code this page's copy can name gets a sentence. An unrecognised code
  // is shown as itself (bounded by the transport), never paraphrased.
  const known = COPY.refusals[reasonCode];
  const description = message ?? known ?? "The settings service refused this request.";

  // A denial is the permission state, not the not-available state: the operator's
  // next step is to ask for `invoice:settings.manage`, not to retry a request the
  // service will refuse identically. The code and reference are the same either
  // way, so support can still trace it.
  if (isDeniedCode(reasonCode)) {
    return (
      <div className={className}>
        <PermissionDeniedState
          title="Access required"
          description={description}
          details={[{ k: "Reason code", v: reasonCode }, ...(capability ? [{ k: "Needs", v: capability }] : [])]}
          action={
            onRetry ? (
              <Button variant="secondary" onClick={onRetry}>
                {retryLabel}
              </Button>
            ) : undefined
          }
        />
        <SupportDetails reference={supportReference} className="mt-3" />
      </div>
    );
  }

  return (
    <div className={className}>
      <NotAvailableState
        title="Invoicing settings could not be read"
        description={description}
        reason={`Reason code: ${reasonCode}`}
      />
      <SupportDetails reference={supportReference} capability={capability} className="mt-3" />
      {onRetry && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/** The billing profile: the KYB legal name, plus the fields the issuer set at onboarding. */
export function BillingProfileCard({ settings }: { settings: InvoicingSettings }) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <CardTitle>{COPY.billingProfileHeading}</CardTitle>

        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-ink">
              {COPY.legalNameLabel}
              <span className="ml-1.5 font-normal text-muted">({COPY.legalNameSource})</span>
            </dt>
            {/* A value, never an input: this is the KYB record, not free text. */}
            <dd className="text-sm text-ink" data-testid="kyb-legal-name">
              {settings.kybLegalName}
            </dd>
          </div>

          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-ink">{COPY.enabledLabel}</dt>
            <dd className="text-sm">
              <Badge variant={settings.enabled ? "success" : "neutral"} dot={false}>
                {settings.enabled ? COPY.enabledOn : COPY.enabledOff}
              </Badge>
            </dd>
          </div>

          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-ink">{COPY.numberPrefixLabel}</dt>
            <dd className="font-mono text-sm text-ink">{settings.numberPrefix}</dd>
          </div>

          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-ink">{COPY.retentionLabel}</dt>
            <dd className="text-sm text-ink">{COPY.retentionValue(settings.retentionYears)}</dd>
          </div>
        </dl>

        <p className="text-xs text-muted">{COPY.billingProfileNote}</p>

        {settings.taxIds && settings.taxIds.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-semibold text-ink">{COPY.taxIdsHeading}</div>
            <ul className="flex flex-col gap-1 text-sm text-ink">
              {settings.taxIds.map((taxId) => (
                <li key={`${taxId.scheme}:${taxId.value}`}>
                  <span className="text-muted">{taxId.scheme}</span>{" "}
                  <span className="font-mono">{taxId.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {settings.billingAddress && (
          <div className="flex flex-col gap-1">
            <div className="text-xs font-semibold text-ink">{COPY.billingAddressHeading}</div>
            <address className="text-sm not-italic text-ink">
              {[
                settings.billingAddress.line1,
                settings.billingAddress.line2,
                settings.billingAddress.city,
                settings.billingAddress.region,
                settings.billingAddress.postalCode,
                settings.billingAddress.country,
              ]
                .filter((line): line is string => typeof line === "string" && line.length > 0)
                .map((line) => (
                  <div key={line}>{line}</div>
                ))}
            </address>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Default terms and expiry grace. The grace bound belongs to the service, not to this form. */
export function TermsCard({
  settings,
  draft,
  onChange,
  editable,
}: {
  settings: InvoicingSettings;
  draft: SettingsDraft;
  onChange: (next: SettingsDraft) => void;
  editable: boolean;
}) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <CardTitle>{COPY.termsHeading}</CardTitle>
        <p className="text-xs text-muted">{COPY.termsNote}</p>

        {editable ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={COPY.defaultTermsLabel} htmlFor="settings-default-terms">
              <Input
                id="settings-default-terms"
                inputMode="numeric"
                value={draft.defaultTermsDays}
                onChange={(event) => onChange({ ...draft, defaultTermsDays: event.target.value })}
              />
            </Field>
            <Field label={COPY.expiryGraceLabel} htmlFor="settings-expiry-grace">
              <Input
                id="settings-expiry-grace"
                inputMode="numeric"
                value={draft.expiryGraceDays}
                onChange={(event) => onChange({ ...draft, expiryGraceDays: event.target.value })}
              />
            </Field>
          </div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold text-ink">{COPY.defaultTermsLabel}</dt>
              <dd className="text-sm text-ink">{settings.defaultTermsDays}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-ink">{COPY.expiryGraceLabel}</dt>
              <dd className="text-sm text-ink">{settings.expiryGraceDays}</dd>
            </div>
          </dl>
        )}

        {settings.reminderSchedule && settings.reminderSchedule.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="text-xs font-semibold text-ink">{COPY.reminderScheduleLabel}</div>
            <div className="text-sm text-ink">{settings.reminderSchedule.join(", ")}</div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The collection wallets. Readiness is the service's verdict (`validatedAt`);
 * a wallet it has not certified is marked as such rather than implied ready.
 */
export function CollectionWalletsCard({
  draft,
  onChange,
  editable,
}: {
  draft: SettingsDraft;
  onChange: (next: SettingsDraft) => void;
  editable: boolean;
}) {
  const setWallet = (index: number, walletId: string) => {
    const next = draft.collectionWallets.map((wallet, i) => (i === index ? { ...wallet, walletId } : wallet));
    onChange({ ...draft, collectionWallets: next });
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <CardTitle>{COPY.walletsHeading}</CardTitle>
        <p className="text-xs text-muted">{COPY.walletsNote}</p>

        {draft.collectionWallets.length === 0 ? (
          <EmptyState title={COPY.walletsEmptyTitle} description={COPY.walletsEmptyBody} />
        ) : (
          <TableWrapper>
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className="px-3 py-2.5">{COPY.walletNetworkColumn}</th>
                  <th className="px-3 py-2.5">{COPY.walletAssetColumn}</th>
                  <th className="px-3 py-2.5">{COPY.walletIdColumn}</th>
                  <th className="px-3 py-2.5">{COPY.walletReadinessColumn}</th>
                </tr>
              </thead>
              <tbody>
                {draft.collectionWallets.map((wallet, index) => {
                  const certified = isCertified(wallet);
                  return (
                    <tr key={`${wallet.network}:${wallet.assetSymbol}`} className="border-b border-line last:border-0">
                      <td className="px-3 py-2.5 text-sm text-ink">{wallet.network}</td>
                      <td className="px-3 py-2.5 text-sm text-ink">{wallet.assetSymbol}</td>
                      <td className="px-3 py-2.5">
                        {editable ? (
                          <label className="sr-only" htmlFor={`wallet-${wallet.network}-${wallet.assetSymbol}`}>
                            {`${COPY.walletIdColumn} for ${wallet.network} ${wallet.assetSymbol}`}
                          </label>
                        ) : null}
                        {editable ? (
                          <Input
                            id={`wallet-${wallet.network}-${wallet.assetSymbol}`}
                            value={wallet.walletId}
                            onChange={(event) => setWallet(index, event.target.value)}
                          />
                        ) : (
                          <span className="font-mono text-sm text-ink">{wallet.walletId}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge variant={certified ? "success" : "warning"} dot={false}>
                          {certified ? COPY.walletCertified : COPY.walletNotCertified}
                        </Badge>
                        {!certified && (
                          <div className="mt-1 text-xs text-muted" data-testid={`wallet-uncertified-${wallet.network}`}>
                            {COPY.walletNotCertifiedHint}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </CardBody>
    </Card>
  );
}

/** Per-network floors. Amounts are edited in the asset's units and sent as base units. */
export function NetworkLimitsCard({
  draft,
  errors,
  onChange,
  editable,
}: {
  draft: SettingsDraft;
  errors: string[];
  onChange: (next: SettingsDraft) => void;
  editable: boolean;
}) {
  const setLimit = (index: number, field: "dustThreshold" | "minInvoiceAmount", value: string) => {
    const next = draft.networkLimits.map((limit, i) => (i === index ? { ...limit, [field]: value } : limit));
    onChange({ ...draft, networkLimits: next });
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <CardTitle>{COPY.limitsHeading}</CardTitle>
        <p className="text-xs text-muted">{COPY.limitsNote}</p>

        {draft.networkLimits.length === 0 ? (
          <EmptyState title={COPY.limitsEmptyTitle} description={COPY.limitsEmptyBody} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {draft.networkLimits.map((limit, index) => (
              <div key={limit.network} className="flex flex-col gap-2 rounded-md border border-line p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{limit.network}</span>
                  <Badge variant="neutral" dot={false}>
                    {limit.assetSymbol}
                  </Badge>
                </div>
                <Field
                  label={COPY.dustLabel}
                  htmlFor={`limit-dust-${limit.network}`}
                  error={errorFor(errors, limit.network, COPY.dustLabel)}
                >
                  <Input
                    id={`limit-dust-${limit.network}`}
                    inputMode="decimal"
                    readOnly={!editable}
                    value={limit.dustThreshold}
                    onChange={(event) => setLimit(index, "dustThreshold", event.target.value)}
                  />
                </Field>
                <Field
                  label={COPY.minInvoiceLabel}
                  htmlFor={`limit-min-${limit.network}`}
                  error={errorFor(errors, limit.network, COPY.minInvoiceLabel)}
                >
                  <Input
                    id={`limit-min-${limit.network}`}
                    inputMode="decimal"
                    readOnly={!editable}
                    value={limit.minInvoiceAmount}
                    onChange={(event) => setLimit(index, "minInvoiceAmount", event.target.value)}
                  />
                </Field>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function errorFor(errors: string[], network: string, label: string): string | undefined {
  return errors.find((error) => error.startsWith(`${network} ${label}`));
}

/** The list of fields the draft could not turn into a wire body. */
export function DraftErrors({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <div className="rounded-lg border border-danger-border bg-danger-soft px-4 py-3 text-sm text-danger" role="alert">
      <div className="font-semibold">{COPY.errorsHeading}</div>
      <ul className="mt-1 list-disc pl-5">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  );
}
